// src/pages/Admin/Adherents/components/AdherentColumnPicker.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiColumns, FiSearch, FiX } from "react-icons/fi";
import { ALL_COLUMNS } from "../columns.config";

export default function AdherentColumnPicker({
  visibleIds,
  setVisibleIds,
  onReset,
  onSelectAll,
  onSelectNone,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const options = useMemo(() => {
    return ALL_COLUMNS.map((column) => ({
      id: column.id,
      label: column.label,
    }));
  }, []);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (!q) return options;

    return options.filter((option) => option.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;

    function onDocumentClick(event) {
      const target = event.target;

      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }

    function onKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const toggleColumn = (id) => {
    setVisibleIds((current) => {
      if (current.includes(id)) {
        return current.filter((item) => item !== id);
      }

      return [...current, id];
    });
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      >
        <FiColumns />
        Colonnes
        <span className="rounded-lg bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {visibleIds.length}/{options.length}
        </span>
      </button>

      {open ? (
        <div
          ref={panelRef}
          className="absolute right-0 z-50 mt-2 w-[min(92vw,24rem)] overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-950"
        >
          <div className="border-b border-slate-200 p-3 dark:border-slate-800">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-white">
                <FiColumns />
                Colonnes visibles
              </div>

              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
              >
                <FiX />
              </button>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onSelectAll}
                className="rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                Tout
              </button>

              <button
                type="button"
                onClick={onSelectNone}
                className="rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                Aucun
              </button>

              <button
                type="button"
                onClick={onReset}
                className="rounded-xl bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 transition hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:hover:bg-indigo-500/20"
              >
                Défaut
              </button>
            </div>

            <div className="relative mt-3">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Rechercher une colonne..."
                className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
            </div>
          </div>

          <div className="max-h-[420px] overflow-auto p-2">
            {filteredOptions.length === 0 ? (
              <div className="p-4 text-sm text-slate-500 dark:text-slate-400">
                Aucune colonne ne correspond à “{query}”.
              </div>
            ) : (
              <div className="space-y-1">
                {filteredOptions.map((option) => {
                  const checked = visibleIds.includes(option.id);

                  return (
                    <label
                      key={option.id}
                      className="flex cursor-pointer items-center gap-3 rounded-2xl px-3 py-2 transition hover:bg-slate-50 dark:hover:bg-slate-900"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleColumn(option.id)}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                      />

                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                        {option.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 px-3 py-2 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
            Les colonnes Adhérent et Actions restent toujours visibles.
          </div>
        </div>
      ) : null}
    </div>
  );
}